'use client'
import * as React from 'react'

// just get types from a worker
import type { WorkerEvent, WorkerMessageResult } from './diff.worker'
import { invariant } from '@epic-web/invariant'
import {
  DiffBetweenRichTexts,
  DiffBetweenRichTextsProps,
  DiffBetweenTrees,
  DiffBetweenTreesProps,
  getDiffBetweenRichTexts,
  getDiffBetweenTrees,
} from './common'

export type WorkersContext = {
  diffWorker?: {
    getDiffBetweenTrees: (
      idempotencyKey: string,
      props: DiffBetweenTreesProps
    ) => Promise<DiffBetweenTrees>
    getDiffBetweenRichTexts: (
      idempotencyKey: string,
      props: DiffBetweenRichTextsProps
    ) => Promise<DiffBetweenRichTexts>
  }
}

const RepoWorkersContext = React.createContext<WorkersContext | null>(null)

export const RepoWorkersProvider = ({
  children,
}: {
  children?: React.ReactNode
}) => {
  const [diffWorker, setDiffWorker] =
    React.useState<WorkersContext['diffWorker']>()

  React.useEffect(() => {
    if (process.env.NEXT_PUBLIC_USE_TURBO === 'true') {
      setDiffWorker({
        getDiffBetweenTrees: async (
          _idempotencyKey: string,
          props: DiffBetweenTreesProps
        ) => {
          return getDiffBetweenTrees(props)
        },
        getDiffBetweenRichTexts: async (
          _idempotencyKey: string,
          props: DiffBetweenRichTextsProps
        ) => {
          return getDiffBetweenRichTexts(props)
        },
      })
      return
    }

    const diffWorker = new Worker(new URL('./diff.worker.ts', import.meta.url))
    const subscribers = new Map<string, (event: WorkerMessageResult) => void>()

    function onMessage(event: MessageEvent<WorkerMessageResult>) {
      subscribers.get(event.data.idempotencyKey)?.(event.data)
    }

    setDiffWorker({
      getDiffBetweenTrees: async (
        idempotencyKey: string,
        props: DiffBetweenTreesProps
      ) => {
        return await new Promise((resolve) => {
          diffWorker.postMessage({
            idempotencyKey,
            action: 'getDiffBetweenTrees',
            props,
          } satisfies WorkerEvent)
          subscribers.set(idempotencyKey, (event) => {
            invariant(
              event.action === 'getDiffBetweenTrees',
              'expected getDiffBetweenTrees action'
            )
            resolve(event.result)
          })
        })
      },
      getDiffBetweenRichTexts: async (
        idempotencyKey: string,
        props: DiffBetweenRichTextsProps
      ) => {
        return await new Promise((resolve) => {
          diffWorker.postMessage({
            idempotencyKey,
            action: 'getDiffBetweenRichTexts',
            props,
          } satisfies WorkerEvent)
          subscribers.set(idempotencyKey, (event) => {
            invariant(
              event.action === 'getDiffBetweenRichTexts',
              'expected getDiffBetweenRichTexts action'
            )
            resolve(event.result)
          })
        })
      },
    })

    diffWorker.addEventListener('message', onMessage)
    return () => {
      diffWorker.removeEventListener('message', onMessage)
      diffWorker.terminate()
    }
  }, [])

  return (
    <RepoWorkersContext.Provider value={{ diffWorker }}>
      {children}
    </RepoWorkersContext.Provider>
  )
}

export const useRepoWorkers = () => {
  const context = React.useContext(RepoWorkersContext)
  if (!context) {
    throw new Error('useRepoWorkers must be used within a RepoWorkersProvider')
  }
  return context
}
